<script lang="ts">
  import { ChevronLeft, ChevronRight, ExternalLink } from '@lucide/svelte'
  import { getContext } from 'svelte'
  import { page, router } from '@inertiajs/svelte'
  import { syncSiteCart } from '~/lib/site-cart'
  import { SITE_CART_KEY, type SiteCartStore } from '~/lib/site-cart-store.svelte'
  import SiteModal from '~/components/site/SiteModal.svelte'
  import ProductPlaceholder from '~/components/site/ProductPlaceholder.svelte'
  import { SITE_UI_KEY, type SiteUiState } from '~/lib/site-ui-context.svelte'
  import { siteBtnPrimaryClass, siteNumberInputNoSpinnerClass } from '~/app/site-theme'

  const siteUi = getContext<SiteUiState>(SITE_UI_KEY)
  const cartStore = getContext<SiteCartStore>(SITE_CART_KEY)

  const product = $derived(siteUi.activeProduct)
  const siteCustomer = $derived(
    $page.props.siteCustomer as { approvalStatus: string } | null | undefined
  )

  let qty = $state(1)
  let imageIndex = $state(0)
  let adding = $state(false)

  const images = $derived(product?.imageUrls ?? [])
  const canOrder = $derived(siteCustomer?.approvalStatus === 'approved')
  const maxQty = $derived(product?.quantity ?? 0)

  $effect(() => {
    if (product) {
      qty = 1
      imageIndex = 0
    }
  })

  async function addToCart() {
    if (!product || maxQty <= 0) return

    if (!siteCustomer) {
      router.visit('/login')
      return
    }

    if (!canOrder) {
      router.visit('/account')
      return
    }

    const safeQty = Math.min(Math.max(1, qty), maxQty)
    adding = true
    const ok = await syncSiteCart(cartStore, product, safeQty, {
      openDrawer: true,
      onSuccess: () => {
        siteUi.closeProduct()
        siteUi.openCartDrawer()
      },
    })
    adding = false
    if (!ok) return
  }

  function prevImage() {
    if (images.length < 2) return
    imageIndex = (imageIndex - 1 + images.length) % images.length
  }

  function nextImage() {
    if (images.length < 2) return
    imageIndex = (imageIndex + 1) % images.length
  }
</script>

<SiteModal bind:open={siteUi.productModalOpen} wide title={product?.name ?? ''}>
  {#if product}
    <div class="grid gap-6 lg:grid-cols-2">
      <div class="space-y-3">
        <div class="relative overflow-hidden rounded-xl border border-[rgba(122,181,71,0.2)] bg-[#182619]">
          {#if images.length > 0}
            <img
              src={images[imageIndex]}
              alt="{product.name} — фото {imageIndex + 1}"
              class="aspect-[4/3] w-full object-cover"
            />
          {:else}
            <div class="aspect-[4/3]">
              <ProductPlaceholder name={product.name} class="h-full w-full" />
            </div>
          {/if}

          {#if images.length > 1}
            <button
              type="button"
              class="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
              aria-label="Предыдущее фото"
              onclick={prevImage}
            >
              <ChevronLeft class="size-5" />
            </button>
            <button
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
              aria-label="Следующее фото"
              onclick={nextImage}
            >
              <ChevronRight class="size-5" />
            </button>
            <span
              class="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2.5 py-0.5 text-xs text-white"
            >
              {imageIndex + 1} / {images.length}
            </span>
          {/if}
        </div>

        {#if images.length > 1}
          <div class="flex gap-2 overflow-x-auto pb-1">
            {#each images as url, i (url)}
              <button
                type="button"
                class="shrink-0 overflow-hidden rounded-lg border-2 transition-colors {i === imageIndex
                  ? 'border-[#7AB547]'
                  : 'border-transparent opacity-70 hover:opacity-100'}"
                onclick={() => (imageIndex = i)}
              >
                <img src={url} alt="" class="size-16 object-cover" />
              </button>
            {/each}
          </div>
        {/if}
      </div>

      <div>
        {#if product.inStock}
          <span
            class="mb-3 inline-flex rounded-full bg-[#7AB547]/15 px-3 py-1 text-xs font-semibold text-[#7AB547]"
          >
            В наличии · {product.quantity} кг
          </span>
        {:else}
          <span
            class="mb-3 inline-flex rounded-full bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-400"
          >
            Нет в наличии
          </span>
        {/if}

        <p class="text-3xl font-bold text-[#7AB547]">
          {product.price}
          <span class="text-base font-normal text-[#dfe8d8]"> ₽ / кг</span>
        </p>

        {#if product.description}
          <p class="mt-4 leading-relaxed text-[#f4f8f1]">{product.description}</p>
        {/if}

        {#if product.inStock}
          <div class="mt-6 rounded-xl border border-[rgba(122,181,71,0.2)] bg-[#09100a]/60 p-4">
            <label class="mb-2 block text-sm font-medium text-[#f4f8f1]" for="modal-qty">
              Количество (кг), макс. {maxQty}
            </label>
            <div
              class="mb-4 inline-flex overflow-hidden rounded-xl border border-[rgba(122,181,71,0.25)]"
            >
              <button
                type="button"
                class="px-3 py-2 text-[#7AB547] hover:bg-[#7AB547]/10 disabled:opacity-30"
                disabled={qty <= 1}
                onclick={() => (qty = Math.max(1, qty - 1))}
              >
                −
              </button>
              <input
                id="modal-qty"
                type="number"
                min="1"
                max={maxQty}
                bind:value={qty}
                class="w-16 bg-transparent py-2 text-center text-sm font-medium text-white focus:outline-none {siteNumberInputNoSpinnerClass}"
              />
              <button
                type="button"
                class="px-3 py-2 text-[#7AB547] hover:bg-[#7AB547]/10 disabled:opacity-30"
                disabled={qty >= maxQty}
                onclick={() => (qty = Math.min(maxQty, qty + 1))}
              >
                +
              </button>
            </div>

            <p class="mb-3 text-sm text-[#dfe8d8]">
              Сумма: <strong class="text-white">{product.price * qty} ₽</strong>
            </p>

            <button
              type="button"
              class="{siteBtnPrimaryClass} w-full"
              disabled={adding}
              onclick={addToCart}
            >
              {adding ? 'Добавляем…' : 'В корзину'}
            </button>
          </div>
        {/if}

        {#if product.slug}
          <a
            href="/product/{product.slug}"
            class="mt-4 inline-flex items-center gap-1 text-sm text-[#7AB547] hover:underline"
          >
            Открыть страницу товара
            <ExternalLink class="size-3.5" />
          </a>
        {/if}
      </div>
    </div>
  {/if}
</SiteModal>
